import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { useTrainingAutoSubmit } from "@/hooks/useTrainingAutoSubmit";
import { useLeadExtractor } from "../../hooks/useLeadExtractor";
import { useLeadBulkTranscript } from "./useLeadBulkTranscript";
import { createLeadExtractionOrchestrator } from "@/lib/lead/actions/extractionOrchestrator";
import { APP_VERSION } from "@/config/version";
import { TRAINING_FEEDBACK_WEBHOOK_URL } from "../../config/webhooks";
import { DeepgramStreamingEngine } from "@/lib/asr/deepgramStreamingEngine";
import type { Lead, Salesperson } from "../../types/lead";
import type { Location } from "../../types/location";
import type { WorkspaceToast } from "../shell/WorkspaceToastOverlay";

type Params = {
  salesperson: Salesperson | null;
  location: Location | null;
  leads: Lead[];
  currentLead: Lead | null;
  transcript: string;
  toast: WorkspaceToast | null;
  setToast: Dispatch<SetStateAction<WorkspaceToast | null>>;
  setPhoneDraft: Dispatch<SetStateAction<string>>;
  scopedOrganizationId: string | null;
  setLeads: Dispatch<SetStateAction<Lead[]>>;
  setCurrentLead: Dispatch<SetStateAction<Lead | null>>;
  setTranscript: Dispatch<SetStateAction<string>>;
};

export function useLeadWorkspaceRuntimeBasics(params: Params) {
  const leadsRef = useRef<Lead[]>([]);
  const salespersonRef = useRef<Salesperson | null>(null);
  const locationRef = useRef<Location | null>(null);
  const extractRequestIdRef = useRef(0);
  const extractionRef = useRef<ReturnType<typeof createLeadExtractionOrchestrator> | null>(null);
  const listeningRef = useRef(false);
  const currentLeadRef = useRef<Lead | null>(null);
  const transcriptRef = useRef("");
  const asrEngineRef = useRef<DeepgramStreamingEngine | null>(null);

  useTrainingAutoSubmit({
    enabled: !!TRAINING_FEEDBACK_WEBHOOK_URL,
    webhookUrl: TRAINING_FEEDBACK_WEBHOOK_URL,
    version: APP_VERSION,
    salespersonRef,
    locationRef,
  });

  useEffect(() => {
    salespersonRef.current = params.salesperson;
  }, [params.salesperson]);
  useEffect(() => {
    locationRef.current = params.location;
  }, [params.location]);
  useEffect(() => {
    currentLeadRef.current = params.currentLead;
  }, [params.currentLead]);
  useEffect(() => {
    transcriptRef.current = params.transcript;
  }, [params.transcript]);
  useEffect(() => {
    leadsRef.current = params.leads;
  }, [params.leads]);

  useEffect(() => {
    if (!params.toast) return;
    // Slice B3: action toasts (e.g. "Lead sent to GHL · Open in CRM")
    // get a longer auto-dismiss so the user has time to read + click
    // through. Plain status toasts keep the historical 3s timing.
    const dismissMs = params.toast.action ? 6000 : 3000;
    const timeout = setTimeout(() => params.setToast(null), dismissMs);
    return () => clearTimeout(timeout);
  }, [params.toast, params.setToast]);

  useEffect(() => {
    const p = params.currentLead?.extracted?.phone || "";
    params.setPhoneDraft((prev) => (prev === p ? prev : p));
  }, [params.currentLead?.id, params.currentLead?.extracted?.phone, params.setPhoneDraft]);

  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => {
      console.log("TTS voices loaded:", window.speechSynthesis.getVoices().length);
    };
  }, []);

  const { extract, extractBulk } = useLeadExtractor({
    orgId: params.scopedOrganizationId ?? null,
  });

  const { createLeadsFromBulkTranscript } = useLeadBulkTranscript({
    extract,
    extractBulk,
    setLeads: params.setLeads,
    setCurrentLead: params.setCurrentLead,
    currentLeadRef,
    setTranscript: params.setTranscript,
    transcriptRef,
    location: params.location,
  });

  return {
    leadsRef,
    salespersonRef,
    locationRef,
    extractRequestIdRef,
    extractionRef,
    listeningRef,
    currentLeadRef,
    transcriptRef,
    asrEngineRef,
    extract,
    createLeadsFromBulkTranscript,
  };
}
